import { ActionType, createReducer } from 'typesafe-actions';
import update from 'immutability-helper';
import { Single_SKUList } from '@nimbly-technologies/nimbly-common';
import * as actions from './skus.action';
import * as types from './skus.actionTypes';
import { SKU_FETCH_STATE, SortDirection, ALL_SKU_LIST } from './type.d';
import { SelectedFilters } from 'components/skuManagement/components/SkuFilter';

export type SkuAction = ActionType<typeof actions>;

export interface SkusState {
	error: string;
	fetchState: SKU_FETCH_STATE;
	filters: SelectedFilters;
	lastUpdatedAt: number;
	limit: number;
	pageIndex: number;
	searchQuery: string;
	skus: Single_SKUList[];
	sortBy: string;
	sortDirection: SortDirection;
	totalSkus: number;
	skusAll: ALL_SKU_LIST[];
	selectedSkuIds: Set<string>;
	isAllSelectedOnPage: boolean;
}

export const initialState: SkusState = {
	error: '',
	fetchState: SKU_FETCH_STATE.SUCCESS,
	filters: { categories: [], sites: [], uoms: [] },
	lastUpdatedAt: 0,
	limit: 15,
	pageIndex: 1,
	searchQuery: '',
	skus: [],
	sortBy: 'name',
	sortDirection: 'asc',
	totalSkus: 0,
	skusAll: [],
	selectedSkuIds: new Set<string>(),
	isAllSelectedOnPage: false,
};

export const skuReducer = createReducer<SkusState, SkuAction>(initialState)
	.handleType(types.SET_SKU_FILTER_QUERY, (state, action) =>
		update(state, {
			fetchState: { $set: SKU_FETCH_STATE.LOADING },
			pageIndex: { $set: initialState.pageIndex },
			filters: { $set: action.payload.filters },
		}),
	)
	.handleType(types.SET_SKU_PAGE_INDEX, (state, action) =>
		update(state, {
			fetchState: { $set: SKU_FETCH_STATE.LOADING },
			pageIndex: { $set: action.payload.pageIndex },
		}),
	)
	.handleType(types.SET_SKU_SEARCH_QUERY, (state, action) =>
		update(state, {
			fetchState: { $set: SKU_FETCH_STATE.LOADING },
			pageIndex: { $set: initialState.pageIndex },
			searchQuery: { $set: action.payload.searchQuery },
		}),
	)
	.handleType(types.SET_SKU_SORT, (state, action) =>
		update(state, {
			fetchState: { $set: SKU_FETCH_STATE.LOADING },
			pageIndex: { $set: initialState.pageIndex },
			sortBy: { $set: action.payload.sortBy },
			sortDirection: { $set: action.payload.sortDirection },
		}),
	)
	.handleType(types.FETCH_SKUS_REQUEST, (state) =>
		update(state, {
			error: { $set: initialState.error },
			fetchState: { $set: SKU_FETCH_STATE.LOADING },
			filters: { $set: initialState.filters },
			pageIndex: { $set: initialState.pageIndex },
			searchQuery: { $set: initialState.searchQuery },
			sortBy: { $set: initialState.sortBy },
			sortDirection: { $set: initialState.sortDirection },
		}),
	)
	.handleType(types.FETCH_SKUS_SUCCESS, (state, action) =>
		update(state, {
			fetchState: { $set: SKU_FETCH_STATE.SUCCESS },
			lastUpdatedAt: { $set: action.payload.lastUpdatedAt },
			skus: { $set: action.payload.skus },
			totalSkus: { $set: action.payload.totalSkus },
		}),
	)
	.handleType(types.FETCH_SKUS_FAILED, (state, action) =>
		update(state, {
			error: { $set: action.payload.error },
			fetchState: { $set: SKU_FETCH_STATE.ERROR },
			skus: { $set: initialState.skus },
		}),
	)
	.handleType(types.FETCH_ALL_SKUS_REQUEST, (state) =>
		update(state, {
			error: { $set: initialState.error },
			fetchState: { $set: SKU_FETCH_STATE.LOADING },
			filters: { $set: initialState.filters },
		}),
	)
	.handleType(types.FETCH_ALL_SKUS_SUCCESS, (state, action) =>
		update(state, {
			fetchState: { $set: SKU_FETCH_STATE.SUCCESS },
			skusAll: { $set: action.payload.skusAll },
		}),
	)
	.handleType(types.FETCH_ALL_SKUS_FAILED, (state, action) =>
		update(state, {
			error: { $set: action.payload.error },
			fetchState: { $set: SKU_FETCH_STATE.ERROR },
			skusAll: { $set: initialState.skusAll },
		}),
	)
	.handleType(types.TOGGLE_SKU_SELECTION, (state, action) => {
		const newSelectedIds = new Set(state.selectedSkuIds);
		if (newSelectedIds.has(action.payload.skuId)) {
			newSelectedIds.delete(action.payload.skuId);
		} else {
			newSelectedIds.add(action.payload.skuId);
		}
		return update(state, {
			selectedSkuIds: { $set: newSelectedIds },
			isAllSelectedOnPage: { $set: false },
		});
	})
	.handleType(types.TOGGLE_ALL_SKUS_ON_PAGE, (state) => {
		const newSelectedIds = new Set(state.selectedSkuIds);
		const currentPageSkuIds = state.skus.map((sku) => sku._id);

		if (state.isAllSelectedOnPage) {
			// Deselect all on current page
			currentPageSkuIds.forEach((id) => newSelectedIds.delete(id));
		} else {
			// Select all on current page
			currentPageSkuIds.forEach((id) => newSelectedIds.add(id));
		}

		return update(state, {
			selectedSkuIds: { $set: newSelectedIds },
			isAllSelectedOnPage: { $set: !state.isAllSelectedOnPage },
		});
	})
	.handleType(types.CLEAR_SKU_SELECTION, (state) =>
		update(state, {
			selectedSkuIds: { $set: new Set<string>() },
			isAllSelectedOnPage: { $set: false },
		}),
	)
	.handleType(types.BULK_DELETE_SKUS_REQUEST, (state) =>
		update(state, {
			fetchState: { $set: SKU_FETCH_STATE.LOADING },
		}),
	)
	.handleType(types.BULK_DELETE_SKUS_SUCCESS, (state, action) => {
		const deletedIds = new Set(action.payload.deletedSkuIds);
		const newSelectedIds = new Set(state.selectedSkuIds);

		// Remove deleted IDs from selection
		action.payload.deletedSkuIds.forEach((id) => newSelectedIds.delete(id));

		// Remove deleted SKUs from the list
		const updatedSkus = state.skus.filter((sku) => !deletedIds.has(sku._id));

		return update(state, {
			fetchState: { $set: SKU_FETCH_STATE.SUCCESS },
			skus: { $set: updatedSkus },
			selectedSkuIds: { $set: newSelectedIds },
			isAllSelectedOnPage: { $set: false },
			totalSkus: { $set: state.totalSkus - action.payload.deletedSkuIds.length },
		});
	})
	.handleType(types.BULK_DELETE_SKUS_FAILED, (state, action) =>
		update(state, {
			error: { $set: action.payload.error },
			fetchState: { $set: SKU_FETCH_STATE.ERROR },
		}),
	);
